import type { AppNotification, ProfileDetails, PtoRequest, TimeOffHistoryRow } from '../types/hr';
import type { EmergencyContact, PayInfo, SelfDoc } from '../types/selfService';

/** The fictional employee used for the "My view" demo login. */
export const SELF_ID = 'e40';

/** Jordan Sample's New Hire PTO ledger (newest first). Ends at the 5.5-day balance in data/timeOff. */
export const selfLedger: TimeOffHistoryRow[] = [
  { id: 'sl10', date: '2026-11-01', description: 'Monthly accrual — October', earned: 1, balance: 5.5, year: 2026 },
  { id: 'sl9', date: '2026-10-16', description: 'Time off — Personal day', used: 1, balance: 4.5, year: 2026 },
  { id: 'sl8', date: '2026-10-01', description: 'Monthly accrual — September', earned: 1, balance: 5.5, year: 2026 },
  { id: 'sl7', date: '2026-09-15', description: 'Adjustment — Volunteer day credit (added by HR)', earned: 0.5, balance: 4.5, year: 2026 },
  { id: 'sl6', date: '2026-09-01', description: 'Monthly accrual — August', earned: 1, balance: 4, year: 2026 },
  { id: 'sl5', date: '2026-08-01', description: 'Monthly accrual — July', earned: 1, balance: 3, year: 2026 },
  { id: 'sl4', date: '2026-07-01', description: 'Monthly accrual — June', earned: 1, balance: 2, year: 2026 },
  { id: 'sl3', date: '2026-06-01', description: 'Monthly accrual — May (prorated, rounded to the nearest half day)', earned: 1, balance: 1, year: 2026 },
  { id: 'sl2', date: '2026-05-06', description: 'Waiting period complete — accrual begins', balance: 0, year: 2026 },
  { id: 'sl1', date: '2026-04-06', description: 'Hired — enrolled in New Hire PTO (30-day waiting period)', balance: 0, year: 2026 },
];

export const selfRequests: PtoRequest[] = [
  { id: 'sr2', employeeId: SELF_ID, start: '2026-12-28', end: '2026-12-28', days: 1, policyId: 'newhire', note: 'Holiday travel.', submitted: '2026-10-20', balanceBefore: 5.5, status: 'approved', approver: 'Alex Example', decidedOn: '2026-10-21' },
  { id: 'sr1', employeeId: SELF_ID, start: '2026-10-16', end: '2026-10-16', days: 1, policyId: 'newhire', note: 'Personal day.', submitted: '2026-10-01', balanceBefore: 5.5, status: 'approved', approver: 'Alex Example', decidedOn: '2026-10-02' },
  { id: 'sr3', employeeId: SELF_ID, start: '2026-09-23', end: '2026-09-25', days: 3, policyId: 'newhire', note: 'Long weekend trip.', submitted: '2026-09-09', balanceBefore: 4, status: 'denied', approver: 'Alex Example', decidedOn: '2026-09-10' },
  { id: 'sr4', employeeId: SELF_ID, start: '2026-08-21', end: '2026-08-21', days: 1, policyId: 'newhire', note: 'Appointment — rescheduled.', submitted: '2026-08-03', balanceBefore: 3, status: 'cancelled', decidedOn: '2026-08-05' },
];

export const selfDocs: SelfDoc[] = [
  { id: 'sd1', name: 'Confidentiality Agreement — signed.pdf', folder: 'signed', uploadedBy: 'HR', date: '2026-04-06', size: '182 KB', signedVia: 'DocuSign' },
  { id: 'sd2', name: 'Arbitration Agreement — signed.pdf', folder: 'signed', uploadedBy: 'HR', date: '2026-04-06', size: '146 KB', signedVia: 'DocuSign' },
  { id: 'sd3', name: 'Offer Letter — Jordan Sample.pdf', folder: 'offer', uploadedBy: 'HR', date: '2026-03-20', size: '98 KB', signedVia: 'DocuSign' },
  { id: 'sd4', name: 'W-4 2026 (Federal).pdf', folder: 'tax', uploadedBy: 'HR', date: '2026-04-06', size: '64 KB' },
  { id: 'sd5', name: 'IT-2104 2026 (State).pdf', folder: 'tax', uploadedBy: 'HR', date: '2026-04-06', size: '58 KB' },
  { id: 'sd6', name: 'Employee Handbook 2026.pdf', folder: 'handbook', uploadedBy: 'HR', date: '2026-01-05', size: '2.4 MB' },
  { id: 'sd7', name: 'New Hire PTO Policy.pdf', folder: 'handbook', uploadedBy: 'HR', date: '2026-01-05', size: '220 KB' },
  { id: 'sd8', name: 'Remote & Hybrid Work Guidelines.pdf', folder: 'handbook', uploadedBy: 'HR', date: '2026-02-11', size: '310 KB' },
  { id: 'sd9', name: '2026 Handbook Acknowledgment', folder: 'handbook', uploadedBy: 'HR', date: '2026-11-02', size: '41 KB', needsSignature: true },
  { id: 'sd10', name: 'Direct deposit form.pdf', folder: 'submitted', uploadedBy: 'Me', date: '2026-04-07', size: '120 KB' },
];

export const selfPay: PayInfo = {
  payType: 'Hourly (non-exempt)',
  rate: '$27.50 / hour',
  overtime: '1.5× after 40 hours per week ($41.25 / hour)',
  schedule: 'Semi-monthly — 15th and last business day of the month',
  nextPayDate: '2026-11-13',
  bank: 'Sample Community Bank · Checking',
  accountLast4: '7310',
};

export const selfEmergencyContact: EmergencyContact = {
  name: 'Robin Sample',
  relationship: 'Partner',
  phone: '(917) 555-0190',
};

/** Keeps the admin Job tab consistent with My Pay for the demo login. */
export const selfProfileOverride: Partial<ProfileDetails> = {
  compensationModel: 'Hourly',
  compensation: [
    { date: 'Apr 6, 2026', schedule: 'Semi-monthly', payType: 'Hourly', rate: '$27.50 / hr', overtime: 'Non-exempt', otRate: '$41.25 / hr', reason: 'New hire', comment: '—' },
  ],
};

export const selfNotifications: AppNotification[] = [
  { id: 'sn1', title: 'Signature needed', body: '2026 Handbook Acknowledgment is waiting for your DocuSign signature.', time: '2d ago', unread: true, route: { page: 'my-documents' } },
  { id: 'sn2', title: 'Time off approved', body: 'Alex Example approved your request for Dec 28.', time: 'Oct 21', unread: true, route: { page: 'my-timeoff', tab: 'requests' } },
  { id: 'sn3', title: 'Upcoming holiday', body: 'Veterans Day — Main Office and Branch – East are closed Nov 11.', time: 'Oct 30', unread: false, route: { page: 'holidays' } },
];
